import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaChevronRight,
  FaBars,
  FaTimes,
  FaSignOutAlt,
  FaSun,
  FaMoon,
  FaSync,
  FaLayerGroup,
  FaCogs,
  FaTable,
  FaCode,
  FaCube,
  FaTachometerAlt,
  FaUsers,
  FaUserShield,
  FaKey,
  FaWrench,
  FaList,
  FaTags,
  FaFileCode, // AGGIUNTO per Richieste modifica software  
  FaCalendarAlt, // AGGIUNTO per Calendario
  FaFlask, // AGGIUNTO per I miei test
  FaBolt, // AGGIUNTO per Generatore Batch (Paul-only)
  FaServer, // AGGIUNTO per Ambienti di test (dashboard ESXi)
} from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { getMenuData } from "../services/menuService";
import Loader from "../common/Loader";

// Mappa delle icone per le diverse funzioni
const FUNCTION_ICONS = {
  // Cruscotto
  'cruscotto': FaTachometerAlt,
  'gruppi': FaUserShield,
  'abilitazioni': FaKey,
  'utenti': FaUsers,
  
  // Gestione SiSketron
  'gestione': FaWrench,
  'macrofunzioni': FaCogs,
  'tabelle': FaTable,
  'generatore': FaCode,
  'scheletri': FaCode,
  'oggetti': FaCube,
  'campi': FaList,
  'moduli': FaLayerGroup,
  'attributi': FaTags,
  
  // AGGIUNTO: Nuove voci per menu secondario
  'richieste': FaFileCode,
  'calendario': FaCalendarAlt,
  'test': FaFlask,
  
  // Default
  'default': FaLayerGroup
};

// Funzione per ottenere l'icona appropriata
const getIconForFunction = (functionName) => {
  if (!functionName) return FUNCTION_ICONS.default;
  
  const name = functionName.toLowerCase();
  
  // Controlli specifici
  if (name.includes('cruscotto')) return FUNCTION_ICONS.cruscotto;
  if (name.includes('gruppi') && name.includes('abilitazioni')) return FUNCTION_ICONS.gruppi;
  if (name.includes('abilitazioni') && name.includes('operazioni')) return FUNCTION_ICONS.abilitazioni;
  if (name.includes('utenti')) return FUNCTION_ICONS.utenti;
  if (name.includes('macrofunzioni') || name.includes('macro funzioni')) return FUNCTION_ICONS.macrofunzioni;
  if (name.includes('tabelle')) return FUNCTION_ICONS.tabelle;
  if (name.includes('generatore') || name.includes('scheletri')) return FUNCTION_ICONS.generatore;
  if (name.includes('oggetti')) return FUNCTION_ICONS.oggetti;
  if (name.includes('campi')) return FUNCTION_ICONS.campi;
  if (name.includes('moduli')) return FUNCTION_ICONS.moduli;
  if (name.includes('attributi')) return FUNCTION_ICONS.attributi;
  
  // AGGIUNTO: Nuove mappature per le voci richieste
  if (name.includes('richieste') && (name.includes('modifica') || name.includes('software'))) return FUNCTION_ICONS.richieste;
  if (name.includes('calendario')) return FUNCTION_ICONS.calendario;
  if (name.includes('test') || name.includes('miei test')) return FUNCTION_ICONS.test;
  
  // 'gestione' è generico, quindi mettilo per ultimo o usalo con più cautela se necessario
  if (name.includes('gestione')) return FUNCTION_ICONS.gestione; 
  
  return FUNCTION_ICONS.default;
};

// Helper per normalizzare le stringhe per il confronto
const normalizeLabelForMatch = (str) => (str || '').toLowerCase().replace(/\s+/g, ' ').trim();

// --- Componente MenuItem ---
// L'elemento button è stilizzato con w-full e padding (py-2, px-3).
// Questo rende cliccabile/toccabile l'intera area visibile dell'elemento.
const MenuItem = ({ node, functionsMap, level, onNavigate, expandedNodes, toggleExpand, location }) => {
  const funcDetails = functionsMap[node.id];
  const isParent = node.children && node.children.length > 0;
  const isExpanded = !!expandedNodes[node.id];

  const checkActive = useCallback((currentNode) => {
    const currentFunc = functionsMap[currentNode.id];
    if (currentFunc && currentFunc.FUA_RottaFrontend === location.pathname) {
      return true;
    }
    if (currentNode.children) {
      return currentNode.children.some(child => checkActive(child));
    }
    return false;
  }, [functionsMap, location.pathname]);

  const isActive = useMemo(() => checkActive(node), [node, checkActive]);

  if (!funcDetails || funcDetails.FUA_RottaFrontend === '/login') return null;

  // Ottieni l'icona appropriata
  const IconComponent = getIconForFunction(funcDetails.FUA_NomeFunzione);

  const baseClasses = "w-full py-2 px-3 rounded-md text-left text-sm font-medium transition-colors duration-150 flex items-center gap-2";
  const activeClasses = `bg-primary/10 text-primary border-l-4 border-primary`;
  const inactiveClasses = `text-gray-600 dark:text-gray-300 border-l-4 border-transparent hover:bg-primary/5 hover:text-primary dark:hover:bg-violet-900/30 dark:hover:text-violet-300`;
  const parentActiveClasses = `text-primary font-semibold`;
  const parentInactiveClasses = `text-gray-700 dark:text-gray-200 hover:bg-primary/5 dark:hover:bg-violet-900/30`;

  if (isParent) {
    return (
      <li className={`mb-0.5 list-none ${level > 0 ? 'mt-1' : 'mt-1.5'}`}>
        {/* This button spans the full width and includes padding */}
        <button
          onClick={() => toggleExpand(node.id)}
          className={`${baseClasses} justify-between ${isActive ? parentActiveClasses : parentInactiveClasses}`}
          style={{ paddingLeft: `${0.75 + level * 0.75}rem` }}
          aria-expanded={isExpanded}
        >
          <div className="flex items-center gap-2 flex-grow truncate">
            <IconComponent size={16} className="flex-shrink-0" />
            <span className="truncate">{funcDetails.FUA_NomeFunzione}</span>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.2 }}
            className={`ml-1 flex-shrink-0 ${isActive ? 'text-primary' : 'text-gray-400'}`}
          >
            <FaChevronRight size={12} />
          </motion.div>
        </button>
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.ul
              key="content"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { opacity: 1, height: "auto" },
                collapsed: { opacity: 0, height: 0 }
              }}
              transition={{ duration: 0.2, ease: [0.04, 0.62, 0.23, 0.98] }}
              className="overflow-hidden list-none pl-0"
            >
              {node.children.map(childNode => (
                <MenuItem
                  key={childNode.id}
                  node={childNode}
                  functionsMap={functionsMap}
                  level={level + 1}
                  onNavigate={onNavigate}
                  expandedNodes={expandedNodes}
                  toggleExpand={toggleExpand}
                  location={location}
                />
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </li>
    );
  } else {
    const isCurrentPage = location.pathname === funcDetails.FUA_RottaFrontend;
    return (
      <li className={`mb-0.5 list-none ${level > 0 ? 'mt-1' : 'mt-1.5'}`}>
        {/* This button spans the full width and includes padding */}
        <motion.button
          onClick={() => onNavigate(funcDetails.FUA_RottaFrontend)}
          whileHover={{ x: 1 }}
          whileTap={{ scale: 0.99 }}
          className={`${baseClasses} ${isCurrentPage ? activeClasses : inactiveClasses}`}
          style={{ paddingLeft: `${0.75 + level * 0.75}rem` }}
        >
          <div className="flex items-center gap-2 flex-grow truncate">
            <IconComponent size={14} className="flex-shrink-0" />
            <span className="truncate">{funcDetails.FUA_NomeFunzione}</span>
          </div>
        </motion.button>
      </li>
    );
  }
};

// --- Componente MinimalMenuButton ---
const MinimalMenuButton = ({ icon: Icon, label, route, isActive, onClick, theme }) => (
  <motion.button
    onClick={onClick}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.95 }}
    className={`relative group w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-200 ${
      isActive 
        ? 'bg-primary text-white shadow-lg' 
        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-primary/10 hover:text-primary dark:hover:bg-violet-900/30 dark:hover:text-violet-300 shadow-md hover:shadow-lg'
    }`}
    title={label}
    aria-label={label}
  >
    <Icon size={18} />
    
    {/* Tooltip */}
    <div className="absolute left-14 top-1/2 transform -translate-y-1/2 bg-gray-900 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
      {label}
      <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-gray-700"></div>
    </div>
  </motion.button>
);

// --- Componente Sidebar ---
export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation(); // Get location here
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [menuData, setMenuData] = useState(null);
  const [functionsMap, setFunctionsMap] = useState({});
  const [loading, setLoading] = useState(true); // Initial loading state
  const [error, setError] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [fullMenuOpen, setFullMenuOpen] = useState(false);

  const userGroupId = user?.gruppoId; // <<< CHANGED HERE

  // Voce Paul-only: visibile solo quando lo username normalizzato è "paul".
  // Non altera la navigazione degli altri utenti (blocco condizionale isolato).
  const isPaul =
    String(user?.UTE_NomeUtente || user?.username || "")
      .trim()
      .toLowerCase() === "paul";
  const GENERATOR_BATCH_ROUTE = "/sisketron/generatore-batch";

  // Voce "Ambienti di test" (dashboard ESXi): visibile a TUTTI gli utenti
  // autenticati, senza alcun controllo per nome utente. Aggiunta staticamente
  // perché la rotta è servita direttamente dal frontend sotto ProtectedLayout.
  const ESXI_DASHBOARD_ROUTE = "/esxi-dashboard";


  // Menu items principali con i loro identificatori
  const mainMenuItems = [
    {
      id: 'gestione-macrofunzioni',
      label: 'Macro Funzioni', // API: "Gestione Macro Funzioni"
      icon: FaCogs,
      searchTerms: ['gestione', 'macro', 'funzioni', 'macrofunzioni']
    },
    {
      id: 'gestione-tabelle',
      label: 'Tabelle', // API: "Gestione Tabelle"
      icon: FaTable,
      searchTerms: ['gestione', 'tabelle', 'tabella']
    },
    {
      id: 'gestione-oggetti',
      label: 'Oggetti', // API: "Gestione Oggetti"
      icon: FaCube,
      searchTerms: ['gestione', 'oggetti', 'oggetto']
    },
    {
      id: 'generatore-scheletri',
      label: 'Generatore codice', // API: "Generatore scheletri"
      icon: FaCode,
      searchTerms: ['generatore', 'scheletri', 'generator']
    },
    // AGGIUNTO: Le 3 voci richieste (rimossa gestione operativa)
    {
      id: 'richieste-modifica-software', 
      label: 'Richieste modifica software',
      icon: FaFileCode,
      searchTerms: ['richieste', 'modifica', 'software', 'change', 'request']
    },
    {
      id: 'calendario',
      label: 'Calendario',
      icon: FaCalendarAlt,
      searchTerms: ['calendario', 'calendar', 'schedule']
    },
    {
      id: 'i-miei-test',
      label: 'I miei test', 
      icon: FaFlask,
      searchTerms: ['test', 'miei', 'testing', 'my tests']
    }
  ];

  const fetchMenuDataForGroup = useCallback(async (gruppoId) => {

    if (!gruppoId) {

        return null; 
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getMenuData(gruppoId); 

      if (!data || !Array.isArray(data.tree) || !Array.isArray(data.funzioni)) {
          console.error('[Sidebar] fetchMenuDataForGroup: Invalid data format from backend.'); // <<< ADD LOG
          throw new Error("Formato dati menu non valido ricevuto dal backend.");
      }
      return data;
    } catch (err) {
      console.error("[Sidebar] Errore in fetchMenuDataForGroup:", err); // <<< MODIFIED LOG
      setError(err.message.includes("Formato dati menu non valido") ? err.message : "Errore durante il caricamento del menu.");
      return null; 
    } finally {
      setLoading(false);
    }
  }, []); 

  // Effect for fetching and processing data when userGroupId changes
  useEffect(() => {

    if (userGroupId) {
      fetchMenuDataForGroup(userGroupId).then(data => {

        if (data) {
          setMenuData(data.tree);
          const funcMap = data.funzioni.reduce((map, func) => {
            if (func.FUA_Id_ProcedureApplicative !== undefined && func.FUA_Id_ProcedureApplicative !== null) {
              map[func.FUA_Id_ProcedureApplicative] = func;
            }
            return map;
          }, {});
          setFunctionsMap(funcMap);
          
          const currentPathname = location.pathname; 
          const initialExpanded = {};
          const findAndExpand = (nodes) => {
             if (!nodes) return false;
             let hasActiveChild = false;
             for (const node of nodes) {
                const func = funcMap[node.id];
                const isCurrentActive = func && func.FUA_RottaFrontend === currentPathname;
                const childActive = findAndExpand(node.children);
                if (isCurrentActive || childActive) {
                   initialExpanded[node.id] = true;
                   hasActiveChild = true;
                }
             }
             return hasActiveChild;
          };
          findAndExpand(data.tree);
          setExpandedNodes(initialExpanded);
        } else {

          setMenuData([]);
          setFunctionsMap({});
          setExpandedNodes({});
        }
      });
    } else {

      setMenuData(null);
      setFunctionsMap({});
      setExpandedNodes({});
      setLoading(false); 
      setError(null); 
    }
  }, [userGroupId, location.pathname, fetchMenuDataForGroup]);

  const handleRefreshMenu = useCallback(() => {

    const currentGroupId = user?.gruppoId; // <<< CHANGED HERE for consistency


    if (currentGroupId && !loading) {

      fetchMenuDataForGroup(currentGroupId).then(data => { // Pass directly

        if (data) {
          setMenuData(data.tree);
          const funcMap = data.funzioni.reduce((map, func) => {
            if (func.FUA_Id_ProcedureApplicative !== undefined && func.FUA_Id_ProcedureApplicative !== null) {
              map[func.FUA_Id_ProcedureApplicative] = func;
            }
            return map;
          }, {});
          setFunctionsMap(funcMap);
        } else {

          setMenuData([]);
          setFunctionsMap({});
        }
      });
    } else {

    }
  }, [user, fetchMenuDataForGroup, loading]); // Ensure 'user' is in dependency array if directly accessing its props


  // Trova la route per un menu item basandosi sui termini di ricerca
  const findRouteForMenuItem = useCallback((menuItemToFind) => {
    if (!functionsMap || Object.keys(functionsMap).length === 0) {
      return null;
    }

    const functionsList = Object.values(functionsMap);
    const normalizedTargetLabel = normalizeLabelForMatch(menuItemToFind.label);
    

    // Tentativo 1: Trova per corrispondenza esatta dell'etichetta normalizzata
    let matchingFunction = functionsList.find(func => {
      const normalizedFuncName = normalizeLabelForMatch(func.FUA_NomeFunzione);
      const isMatch = normalizedFuncName === normalizedTargetLabel;

      return isMatch;
    });

    if (matchingFunction) {

      return matchingFunction.FUA_RottaFrontend;
    }

    // Tentativo 2: Fallback usando searchTerms se non c'è corrispondenza esatta dell'etichetta
    if (!matchingFunction && menuItemToFind.searchTerms && menuItemToFind.searchTerms.length > 0) {
      // No exact label match, falling back to searchTerms
      
      // Trova funzioni dove TUTTI i termini di ricerca sono inclusi in FUA_NomeFunzione
      const potentialMatchesAllTerms = functionsList.filter(func => {
        const normalizedFuncLabel = normalizeLabelForMatch(func.FUA_NomeFunzione);
        const allTermsMatch = menuItemToFind.searchTerms.every(term => {
          const normalizedTerm = normalizeLabelForMatch(term);
          const termMatch = normalizedFuncLabel.includes(normalizedTerm);
          return termMatch;
        });
        return allTermsMatch;
      });

      if (potentialMatchesAllTerms.length === 1) {
        matchingFunction = potentialMatchesAllTerms[0];
      } else if (potentialMatchesAllTerms.length > 1) {
        potentialMatchesAllTerms.sort((a, b) => normalizeLabelForMatch(a.FUA_NomeFunzione).length - normalizeLabelForMatch(b.FUA_NomeFunzione).length);
        matchingFunction = potentialMatchesAllTerms[0];
        // Multiple fallback matches, selected shortest
      } else {
        // No matches found using all search terms
        
        // Se non tutti i termini corrispondono, prova se ALCUNI termini corrispondono (logica più ampia originale, come ultima risorsa)
        const potentialMatchesSomeTerms = functionsList.filter(func => {
          const normalizedFuncLabel = normalizeLabelForMatch(func.FUA_NomeFunzione);
          return menuItemToFind.searchTerms.some(term => normalizedFuncLabel.includes(normalizeLabelForMatch(term)));
        });
        
        if (potentialMatchesSomeTerms.length === 1) {
          matchingFunction = potentialMatchesSomeTerms[0];
        } else if (potentialMatchesSomeTerms.length > 1) {
          potentialMatchesSomeTerms.sort((a, b) => normalizeLabelForMatch(a.FUA_NomeFunzione).length - normalizeLabelForMatch(b.FUA_NomeFunzione).length);
          matchingFunction = potentialMatchesSomeTerms[0];
          // Multiple partial matches, selected shortest
        }
      }
    }
    
    if (!matchingFunction) {
      console.warn(`[Sidebar] Could not find a route for menu item: "${menuItemToFind.label}"`);
      return null;
    }

    if (!matchingFunction.FUA_RottaFrontend || matchingFunction.FUA_RottaFrontend.trim() === "") {
      console.warn(`[Sidebar] Matching function "${matchingFunction.FUA_NomeFunzione}" for "${menuItemToFind.label}" has no valid FUA_RottaFrontend.`);
      return null;
    }
    
    return matchingFunction.FUA_RottaFrontend;
  }, [functionsMap]);


  const handleNavigate = (route) => {
    if (route && route !== location.pathname) {
      navigate(route);
      setFullMenuOpen(false);
    }
  };

  const handleMinimalMenuClick = (menuItem) => {
    const route = findRouteForMenuItem(menuItem);
    if (route) {
      handleNavigate(route);
    } else {
      console.warn(`[Sidebar] No route found for minimal menu item: ${menuItem.label}`);
      // Potresti voler navigare a una pagina di default o mostrare un errore
      // Per ora, non fa nulla se la rotta non viene trovata
    }
  };

  const toggleExpand = (nodeId) => {
    setExpandedNodes(prev => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  const toggleFullMenu = () => setFullMenuOpen(!fullMenuOpen);

  // Controlla se un menu item è attivo
  const isMenuItemActive = (menuItem) => {
    const route = findRouteForMenuItem(menuItem);
    return route === location.pathname;
  };

  // --- Render del contenuto del menu completo ---
  const renderFullMenuContent = () => {
    if (loading && !menuData) {
      return <div className="flex justify-center items-center h-32"><Loader size={8} /></div>;
    }
    if (error) {
      return <div className="p-4 text-center text-red-600 dark:text-red-400">{error}</div>;
    }
    if (menuData === null || menuData.length === 0) {
      if (!loading && user) {
        return <div className="p-4 text-center text-gray-500 dark:text-gray-400">Nessuna voce di menu disponibile.</div>;
      }
      return null;
    }
    return (
      <ul className="list-none p-0 m-0">
        {menuData.map(node => (
          <MenuItem
            key={node.id}
            node={node}
            functionsMap={functionsMap}
            level={0}
            onNavigate={handleNavigate}
            expandedNodes={expandedNodes}
            toggleExpand={toggleExpand}
            location={location}
          />
        ))}
      </ul>
    );
  };

  return (
    <>
      {/* Sidebar Minimizzata - Sempre visibile TRANNE quando il menu è aperto */}
      <AnimatePresence>
        {!fullMenuOpen && (
          <motion.div
            initial={{ opacity: 1, x: 0 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="fixed left-0 top-0 h-screen w-16 bg-white dark:bg-gray-800 shadow-lg border-r border-gray-200 dark:border-gray-700 z-40 flex flex-col"
          >
            {/* Header con Hamburger */}
            <div className="flex items-center justify-center h-16 border-b border-gray-200 dark:border-gray-700">
              <motion.button
                onClick={toggleFullMenu}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="w-10 h-10 rounded-lg bg-primary text-white shadow-md hover:bg-primary/80 transition-all duration-200 flex items-center justify-center"
                aria-label="Espandi menu"
                title="Espandi menu completo"
              >
                <FaBars size={18} />
              </motion.button>
            </div>

            {/* Icone Menu Principali */}
            <nav className="flex-1 flex flex-col gap-3 p-3 overflow-y-auto">
              {mainMenuItems.map((menuItem, index) => (
                <motion.button
                  key={menuItem.id}
                  onClick={() => handleMinimalMenuClick(menuItem)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ 
                    opacity: 1, 
                    y: 0,
                    transition: { delay: index * 0.05, duration: 0.3 }
                  }}
                  className={`relative group w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-200 ${
                    isMenuItemActive(menuItem)
                      ? 'bg-primary text-white shadow-lg' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-primary/10 hover:text-primary dark:hover:bg-violet-900/30 dark:hover:text-violet-300 shadow-sm hover:shadow-md'
                  }`}
                  title={menuItem.label}
                  aria-label={menuItem.label}
                >
                  <menuItem.icon size={16} />
                  
                  {/* Tooltip laterale */}
                  <div className="absolute left-16 top-1/2 transform -translate-y-1/2 bg-gray-900 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 ml-2">
                    {menuItem.label}
                    <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-gray-700"></div>
                  </div>
                </motion.button>
              ))}

              {/* Ambienti di test (dashboard ESXi) — visibile a tutti gli utenti */}
              <motion.button
                onClick={() => handleNavigate(ESXI_DASHBOARD_ROUTE)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`relative group w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-200 ${
                  location.pathname === ESXI_DASHBOARD_ROUTE
                    ? 'bg-primary text-white shadow-lg'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-primary/10 hover:text-primary dark:hover:bg-violet-900/30 dark:hover:text-violet-300 shadow-sm hover:shadow-md'
                }`}
                title="Ambienti di test"
                aria-label="Ambienti di test"
              >
                <FaServer size={16} />
                <div className="absolute left-16 top-1/2 transform -translate-y-1/2 bg-gray-900 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 ml-2">
                  Ambienti di test
                  <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-gray-700"></div>
                </div>
              </motion.button>

              {/* Generatore Batch — Paul-only (isolato, non tocca gli altri utenti) */}
              {isPaul && (
                <motion.button
                  onClick={() => handleNavigate(GENERATOR_BATCH_ROUTE)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`relative group w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-200 ${
                    location.pathname === GENERATOR_BATCH_ROUTE
                      ? 'bg-primary text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-primary/10 hover:text-primary dark:hover:bg-violet-900/30 dark:hover:text-violet-300 shadow-sm hover:shadow-md'
                  }`}
                  title="Generatore Batch"
                  aria-label="Generatore Batch"
                >
                  <FaBolt size={16} />
                  <div className="absolute left-16 top-1/2 transform -translate-y-1/2 bg-gray-900 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 ml-2">
                    Generatore Batch
                    <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-gray-700"></div>
                  </div>
                </motion.button>
              )}
            </nav>

            {/* Footer con controlli utente - RIMUOVERE LOGOUT E TEMA */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { delay: 0.3 } }}
              className="p-3 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-2"
            >
              {/* RIMOSSO: Toggle Tema e Logout - già presenti in Header */}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Menu Esteso (Overlay) */}
      <AnimatePresence>
        {fullMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-30 bg-black/30 backdrop-blur-sm"
              onClick={toggleFullMenu}
              aria-hidden="true"
            />

            {/* Menu completo esteso */}
            <motion.aside
              initial={{ x: -400, opacity: 0 }}
              animate={{ 
                x: 0, 
                opacity: 1,
                transition: { 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 30,
                  opacity: { duration: 0.2 }
                }
              }}
              exit={{ 
                x: -400, 
                opacity: 0,
                transition: { 
                  duration: 0.3,
                  ease: "easeInOut"
                }
              }}
              className="fixed top-0 left-0 z-40 h-screen w-96 flex flex-col bg-white dark:bg-gray-800 shadow-2xl border-r border-gray-200 dark:border-gray-700"
              aria-label="Menu completo"
            >
              {/* Header del menu esteso */}
              <motion.div 
                initial={{ y: -20, opacity: 0 }}
                animate={{ 
                  y: 0, 
                  opacity: 1,
                  transition: { delay: 0.1, duration: 0.3 }
                }}
                className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0"
              >
                <div className="flex items-center gap-3">
                  <FaLayerGroup className="text-primary text-3xl" />
                  <div>
                    <h1 className="text-xl font-bold text-gray-800 dark:text-white">SiSketron</h1>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Menu Completo</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleRefreshMenu}
                    disabled={loading}
                    className={`p-2 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                    aria-label="Aggiorna menu"
                  >
                    <FaSync className={`${loading ? 'animate-spin' : ''}`} size={16} />
                  </button>
                  <motion.button
                    onClick={toggleFullMenu}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-2 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors"
                    aria-label="Chiudi menu"
                  >
                    <FaTimes size={16} />
                  </motion.button>
                </div>
              </motion.div>

              {/* Contenuto del menu esteso */}
              <motion.nav 
                initial={{ opacity: 0 }}
                animate={{ 
                  opacity: 1,
                  transition: { delay: 0.2, duration: 0.4 }
                }}
                className="flex-grow overflow-y-auto p-4 space-y-1" 
                aria-label="Navigazione principale"
              >
                {/* Ambienti di test (dashboard ESXi) — visibile a tutti gli utenti */}
                <button
                  type="button"
                  onClick={() => handleNavigate(ESXI_DASHBOARD_ROUTE)}
                  className={`mb-2 flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    location.pathname === ESXI_DASHBOARD_ROUTE
                      ? 'border-primary bg-primary text-white shadow-sm'
                      : 'border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 dark:bg-primary/10'
                  }`}
                >
                  <FaServer size={16} />
                  <span className="flex-1">Ambienti di test</span>
                  <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                    Nuovo
                  </span>
                </button>

                {/* Generatore Batch — Paul-only (blocco isolato sopra il menu dinamico) */}
                {isPaul && (
                  <button
                    type="button"
                    onClick={() => handleNavigate(GENERATOR_BATCH_ROUTE)}
                    className={`mb-2 flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                      location.pathname === GENERATOR_BATCH_ROUTE
                        ? 'border-primary bg-primary text-white shadow-sm'
                        : 'border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 dark:bg-primary/10'
                    }`}
                  >
                    <FaBolt size={16} />
                    <span className="flex-1">Generatore Batch</span>
                    <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                      Nuovo
                    </span>
                  </button>
                )}
                {renderFullMenuContent()}
              </motion.nav>

              {/* Footer del menu esteso - RIMUOVERE LOGOUT E TEMA */}
              <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ 
                  y: 0, 
                  opacity: 1,
                  transition: { delay: 0.3, duration: 0.3 }
                }}
                className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-3 shrink-0"
              >
                {user && (
                  <div className="text-xs text-center text-gray-500 dark:text-gray-400 truncate px-2" title={user.UTE_NomeUtente || user.username}>
                    Utente: {user.UTE_NomeUtente || user.username}
                  </div>
                )}
                {/* RIMOSSI: pulsanti tema e logout - già presenti in Header */}
              </motion.div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}